import type { ReactNode } from "react";
import { Link } from "react-router";
import { useAuth, useCharacterQuery, useOpenCharacters, useSystems } from "../api/queries";
import type { OpenCharacterView, TermLanguage } from "../api/types";
import { CharacterProvider, useCharacter } from "../character";
import { characterPath } from "../components/OpenCharacters";
import { systemUi } from "../registry";
import { useCurrentUser } from "./AuthPages";
import { CharacterBadges } from "./CharacterLayout";

/** Changes made on other devices, e.g. points struck off at the table, show up within this time. */
const refreshEvery = 10_000;

/** The open characters side by side with their most important values, e.g. for the game master at the table. */
export function GroupPage() {
  const open = useOpenCharacters();
  const list = open.data ?? [];
  return (
    <section>
      <header className="page-header">
        <h1>Gruppe</h1>
      </header>
      {open.isLoading && <p className="muted">Lade …</p>}
      {open.data && list.length === 0 && (
        <p className="muted">
          Keine offenen Charaktere. Öffne die Charaktere der Runde aus der <Link to="/">Liste</Link>, dann stehen sie hier nebeneinander.
        </p>
      )}
      {list.length > 0 && <p className="muted small">Änderungen von anderen Geräten erscheinen nach wenigen Sekunden.</p>}
      <ul className="group-list" aria-label="Charaktere der Gruppe">
        {list.map((c) => (
          <li key={c.id}>
            <GroupMember open={c} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function GroupMember({ open }: { open: OpenCharacterView }) {
  return <GroupCharacter id={open.id} name={open.name} path={characterPath(open)} />;
}

/**
 * A character of a group with its most important values, refreshed now and then. Without access to the character the card shows
 * the fallback, or that it was not loaded.
 */
export function GroupCharacter(props: { id: string; name: string; path: string; fallback?: ReactNode }) {
  const character = useCharacterQuery(props.id, refreshEvery);
  const systems = useSystems();
  const auth = useAuth();
  const language = (auth.data?.user?.termLanguage ?? "de") as TermLanguage;
  if (!character.data) {
    if (character.error && props.fallback) return <>{props.fallback}</>;
    return (
      <article className="card group-card" aria-label={props.name}>
        <h2>
          <Link to={props.path}>{props.name}</Link>
        </h2>
        <p className={character.error ? "error" : "muted"}>{character.error ? "Nicht geladen." : "Lade …"}</p>
      </article>
    );
  }
  return (
    <CharacterProvider character={character.data} system={systems.data?.find((s) => s.id === character.data.system)} language={language}>
      <GroupCard path={props.path} />
    </CharacterProvider>
  );
}

function GroupCard(props: { path: string }) {
  const { character } = useCharacter();
  const user = useCurrentUser();
  const Values = systemUi(character.system)?.groupCard;
  const { subtitle, level } = character.evaluation.summary;
  return (
    <article className="card group-card" aria-label={character.name}>
      <header className="group-card-header">
        <h2>
          <Link to={props.path}>{character.name}</Link>
        </h2>
        <p className="muted small">{[subtitle, level].filter(Boolean).join(" · ")}</p>
        {character.ownerId !== user.id && <p className="muted small">gehört {character.ownerName}</p>}
        <div className="character-status">
          <CharacterBadges />
        </div>
      </header>
      {Values && <Values />}
    </article>
  );
}
